import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, DatabaseZap, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { handleApiError } from '@/api/client'
import {
  apiErrorCode,
  executeCrmRebuild,
  fetchCrmRebuildStatus,
  previewCrmRebuild,
  type CrmRebuildPreview,
} from '@/api/lcCrm'
import { formatKstDateTime } from './crmLabels'

const CONFIRM_WORD = '재구축'

const TABLE_LABEL: Record<string, string> = {
  lc_crm_contact: '연락처',
  lc_crm_intake: '접수',
  lc_crm_lead: '리드',
  lc_crm_stage_history: '리드 단계 이력',
  lc_crm_consultation: '상담',
  lc_crm_attachment: '상담 첨부',
  lc_crm_task: '할 일',
  lc_crm_lead_document: '리드 원본 문서',
  lc_crm_contract_transition: '계약 전환',
  lc_crm_merge_review: '병합 검토',
  lc_crm_merge_event: '병합 이벤트',
  lc_crm_audit_log: '감사 로그',
  lc_crm_verification: 'OTP 세션',
  lc_crm_import_batch: 'import 배치',
  lc_crm_import_row: 'import 행',
}

/**
 * 접수함 재구축 (ADR-0008). CRM 전체를 비우고 원장(설명회 신청·가맹 문의)만으로 연락처·접수를 다시
 * 만든다. 리드·상담은 만들지 않는다.
 *
 * 되돌릴 수 없는 삭제라 미리보기 → 확인 단어 입력 → 실행 세 단계이고, 실행 버튼은 미리보기 토큰이
 * 있을 때만 그려진다. 서버 설정이 꺼져 있으면 미리보기부터 403이다.
 */
export function CrmRebuildCard() {
  const queryClient = useQueryClient()
  const [preview, setPreview] = useState<CrmRebuildPreview | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmWord, setConfirmWord] = useState('')
  // 실행을 요청한 뒤에만 완료 시점에 CRM 화면을 새로 읽는다 — 페이지를 열 때 DONE이 보이는 건 지난 실행이다.
  const [awaitingCompletion, setAwaitingCompletion] = useState(false)

  const status = useQuery({
    queryKey: ['lc-crm-rebuild-status'],
    queryFn: fetchCrmRebuildStatus,
    refetchInterval: (query) => (query.state.data?.state === 'RUNNING' ? 2_000 : false),
  })
  const running = status.data?.state === 'RUNNING'

  useEffect(() => {
    if (!awaitingCompletion || !status.data || status.data.state === 'RUNNING') return
    setAwaitingCompletion(false)
    if (status.data.state === 'DONE' && status.data.result) {
      const r = status.data.result
      toast.success(`재구축이 끝났습니다. 접수 고객 ${r.createdContacts.toLocaleString('ko-KR')}명 · 접수 ${r.createdIntakes.toLocaleString('ko-KR')}건`)
      void queryClient.invalidateQueries()
    } else if (status.data.state === 'FAILED') {
      toast.error(
        status.data.errorCode === 'CRM_REBUILD_STALE'
          ? '미리보기 이후 CRM 데이터가 바뀌어 실행이 거절됐습니다. 미리보기를 다시 만드세요.'
          : `재구축 실패: ${status.data.errorMessage ?? status.data.errorCode ?? '알 수 없는 오류'}`,
      )
    }
  }, [awaitingCompletion, status.data, queryClient])

  const previewMutation = useMutation({
    mutationFn: previewCrmRebuild,
    onSuccess: (data) => {
      setPreview(data)
    },
    onError: (error) => {
      setPreview(null)
      toast.error(
        apiErrorCode(error) === 'CRM_REBUILD_DISABLED'
          ? '재구축이 꺼져 있습니다. 서버 설정 LC_CRM_REBUILD_ENABLED=true로 운영 창을 열어야 합니다.'
          : handleApiError(error) || '재구축 미리보기 실패',
      )
    },
  })

  const executeMutation = useMutation({
    mutationFn: (token: string) => executeCrmRebuild(token),
    onSuccess: async () => {
      setPreview(null)
      setConfirmOpen(false)
      setConfirmWord('')
      setAwaitingCompletion(true)
      toast.info('재구축을 시작했습니다. 끝날 때까지 상태를 확인합니다.')
      await queryClient.invalidateQueries({ queryKey: ['lc-crm-rebuild-status'] })
    },
    onError: (error) => {
      const code = apiErrorCode(error)
      if (code === 'CRM_REBUILD_STALE') {
        toast.error('미리보기 이후 CRM 데이터가 바뀌었습니다. 미리보기를 다시 만드세요.')
        setPreview(null)
        setConfirmOpen(false)
        return
      }
      if (code === 'CRM_REBUILD_ALREADY_RUNNING') {
        toast.error('이미 재구축이 돌고 있습니다. 끝난 뒤 다시 시도하세요.')
        setConfirmOpen(false)
        void queryClient.invalidateQueries({ queryKey: ['lc-crm-rebuild-status'] })
        return
      }
      toast.error(handleApiError(error) || '재구축 실패')
    },
  })

  const result = status.data?.state === 'DONE' ? status.data.result ?? null : null

  const deleteRows = preview
    ? Object.entries(preview.deleteCounts).filter(([, count]) => count > 0)
    : []

  return (
    <Card>
      <CardHeader>
        <CardTitle>접수함 재구축 (원장 기준)</CardTitle>
        <CardDescription>
          CRM 데이터를 전부 비우고 문의관리의 설명회 신청·가맹 문의 원장만으로 연락처와 접수를 다시
          만듭니다. 리드·상담·할 일은 만들지 않습니다.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>되돌릴 수 없습니다</AlertTitle>
          <AlertDescription>
            리드·상담·할 일·병합 검토·import 이력이 모두 삭제됩니다. 실행 전에 DB 스냅샷을 남기고, 서버
            설정 <code>LC_CRM_REBUILD_ENABLED</code>이 켜진 동안만 동작합니다.
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            disabled={previewMutation.isPending || running}
            onClick={() => previewMutation.mutate()}
          >
            {previewMutation.isPending ? <Spinner /> : <RefreshCw className="size-4" />}
            재구축 미리보기
          </Button>
          {running ? (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner /> 재구축 진행 중 · {status.data?.startedAt ? formatKstDateTime(status.data.startedAt) : ''} 시작
            </span>
          ) : null}
          {status.data?.state === 'FAILED' ? (
            <span className="text-sm text-destructive">
              마지막 실행 실패 · {status.data.errorCode ?? ''} {status.data.errorMessage ?? ''}
            </span>
          ) : null}
        </div>

        {preview ? (
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-medium text-foreground">삭제 예정</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>대상</TableHead>
                      <TableHead className="text-right">행</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deleteRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="py-6 text-center text-muted-foreground">
                          지울 데이터가 없습니다
                        </TableCell>
                      </TableRow>
                    ) : deleteRows.map(([table, count]) => (
                      <TableRow key={table}>
                        <TableCell>{TABLE_LABEL[table] ?? table}</TableCell>
                        <TableCell className="text-right tabular-nums">{count.toLocaleString('ko-KR')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-medium text-foreground">원장 기준 생성 예정</h3>
                <LedgerTable ledger={preview.ledger} />
              </section>
            </div>
            <p className="text-xs text-muted-foreground">
              미리보기는 {formatKstDateTime(preview.expiresAt)}까지 유효합니다. 그 사이 CRM 데이터가 바뀌면 실행이
              거절되고 미리보기를 다시 만들어야 합니다.
            </p>
            <div>
              <Button variant="destructive" disabled={running} onClick={() => setConfirmOpen(true)}>
                <DatabaseZap className="size-4" />
                재구축 실행
              </Button>
            </div>
          </div>
        ) : null}

        {result ? (
          <Alert>
            <DatabaseZap />
            <AlertTitle>마지막 재구축 완료 · {formatKstDateTime(result.executedAt)}</AlertTitle>
            <AlertDescription>
              접수 고객 {result.createdContacts.toLocaleString('ko-KR')}명 (설명회만 {result.ledger.briefingOnlyContacts} ·
              문의만 {result.ledger.franchiseOnlyContacts} · 둘 다 {result.ledger.bothContacts}) ·
              접수 {result.createdIntakes.toLocaleString('ko-KR')}건 (설명회 {result.createdBriefingIntakes} ·
              문의 {result.createdFranchiseIntakes} · 취소 {result.cancelledIntakes})
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!open) { setConfirmOpen(false); setConfirmWord('') } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>정말 재구축합니까?</DialogTitle>
            <DialogDescription>
              CRM 데이터 전부를 지우고 원장으로 다시 만듭니다. 되돌릴 수 없습니다. 확인을 위해
              <strong> {CONFIRM_WORD}</strong>를 입력하세요.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="crm-rebuild-confirm">확인 단어</FieldLabel>
            <Input
              id="crm-rebuild-confirm"
              value={confirmWord}
              onChange={(event) => setConfirmWord(event.target.value)}
              placeholder={CONFIRM_WORD}
              autoComplete="off"
            />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setConfirmWord('') }}>취소</Button>
            {preview ? (
              <Button
                variant="destructive"
                disabled={confirmWord.trim() !== CONFIRM_WORD || executeMutation.isPending}
                onClick={() => executeMutation.mutate(preview.previewToken)}
              >
                {executeMutation.isPending ? <Spinner /> : <DatabaseZap className="size-4" />}
                삭제하고 재구축 시작
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function LedgerTable({ ledger }: { ledger: CrmRebuildPreview['ledger'] }) {
  const rows: Array<[string, number]> = [
    ['설명회 신청 (원장)', ledger.briefingRows],
    ['  그중 취소', ledger.briefingCancelledRows],
    ['가맹 문의 (원장)', ledger.franchiseRows],
    ['→ 접수', ledger.expectedIntakes],
    ['→ 접수 고객', ledger.expectedContacts],
    ['  설명회만', ledger.briefingOnlyContacts],
    ['  문의만', ledger.franchiseOnlyContacts],
    ['  둘 다', ledger.bothContacts],
  ]
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>항목</TableHead>
          <TableHead className="text-right">수</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell className="whitespace-pre">{label}</TableCell>
            <TableCell className="text-right tabular-nums">{value.toLocaleString('ko-KR')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
